import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Button, Card, Input, Select, Spinner, Empty, Badge, fmtBRL, fmtNum } from "@/components/ui/Primitives";
import { api, formatErr, fmtDate } from "@/lib/api";
import { Plus, X, Pencil, Trash2, Package2 } from "lucide-react";

const empty = { tipo: "Genérica", cor: "Amarela", quantidade: "", preco_unitario: "", fornecedor: "", data_compra: new Date().toISOString().slice(0, 10), data_pagamento: "", nf_fornecedor: "", observacoes: "" };

export default function Sacarias() {
  const [stock, setStock] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([api.get("/sacarias/stock"), api.get("/sacarias/purchases")]);
      setStock(s.data); setPurchases(p.data);
    } catch (e) { setErr(formatErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); setErr(""); };
  const openEdit = (p) => { setEditing(p); setForm({ ...empty, ...p }); setOpen(true); setErr(""); };
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = { ...form, quantidade: Number(form.quantidade) || 0, preco_unitario: Number(form.preco_unitario) || 0 };
      if (editing) await api.put(`/sacarias/purchases/${editing.id}`, payload);
      else await api.post("/sacarias/purchases", payload);
      setOpen(false); await load();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Excluir compra?")) return;
    try { await api.delete(`/sacarias/purchases/${id}`); await load(); }
    catch (e) { alert(formatErr(e)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const total = (Number(form.quantidade) || 0) * (Number(form.preco_unitario) || 0);

  return (
    <Layout
      title="Sacarias"
      subtitle="Compras e estoque de embalagens"
      actions={<Button onClick={openNew} data-testid="new-sacaria-button"><Plus size={16} /> Nova compra</Button>}
    >
      {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md mb-4">{err}</div>}
      {loading ? <div className="flex justify-center py-20"><Spinner size={28} /></div> : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {(stock?.items || []).map((s) => (
              <Card key={s.tipo} className="p-5" data-testid={`sacaria-stock-${s.tipo.toLowerCase()}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Package2 size={18} className="text-brand-500" />
                    <h3 className="font-heading font-bold text-ink-900">Sacaria {s.tipo}</h3>
                  </div>
                  <Badge tone={s.disponivel > 0 ? "success" : "warn"}>{fmtNum(s.disponivel, 0)} disp.</Badge>
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm mt-3">
                  <div><div className="text-xs text-ink-500 uppercase tracking-wider">Comprado</div><div className="font-mono text-lg font-semibold">{fmtNum(s.comprado, 0)}</div></div>
                  <div><div className="text-xs text-ink-500 uppercase tracking-wider">Usado</div><div className="font-mono text-lg font-semibold text-warn">{fmtNum(s.consumido, 0)}</div></div>
                  <div><div className="text-xs text-ink-500 uppercase tracking-wider">Preço médio</div><div className="font-mono text-lg font-semibold">{fmtBRL(s.preco_medio)}</div></div>
                </div>
                <div className="text-xs text-ink-500 mt-2">Valor em estoque: <span className="font-mono text-ink-900 font-semibold">{fmtBRL(s.valor_estoque)}</span></div>
              </Card>
            ))}
          </div>

          {purchases.length === 0 ? (
            <Card><Empty title="Sem compras de sacaria" hint="Cadastre a primeira compra para alimentar o estoque." /></Card>
          ) : (
            <Card className="overflow-hidden">
              <div className="px-6 py-4 border-b border-tertiary"><h3 className="font-heading font-bold text-ink-900">Histórico de compras</h3></div>
              <table className="w-full text-sm">
                <thead className="bg-stone-50 border-b border-tertiary">
                  <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                    <th className="px-4 py-3">Data</th>
                    <th className="px-4 py-3">Tipo</th>
                    <th className="px-4 py-3">Cor</th>
                    <th className="px-4 py-3">Fornecedor</th>
                    <th className="px-4 py-3 text-right">Qtd</th>
                    <th className="px-4 py-3 text-right">Preço un.</th>
                    <th className="px-4 py-3 text-right">Total</th>
                    <th className="px-4 py-3 w-1">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {purchases.map((p) => (
                    <tr key={p.id} className="border-b border-tertiary last:border-0 hover:bg-stone-50" data-testid={`sacaria-row-${p.id}`}>
                      <td className="px-4 py-3 font-mono">{fmtDate(p.data_compra)}</td>
                      <td className="px-4 py-3"><Badge tone={p.tipo === "Serenata" ? "success" : "neutral"}>{p.tipo}</Badge></td>
                      <td className="px-4 py-3">
                        {p.cor && (
                          <span className="inline-flex items-center gap-1.5 text-xs">
                            <span className={`inline-block w-3 h-3 rounded-full border border-ink-300 ${
                              p.cor === "Amarela" ? "bg-yellow-400"
                              : p.cor === "Branca" ? "bg-white"
                              : p.cor === "Azul" ? "bg-blue-500"
                              : p.cor === "Preta" ? "bg-black"
                              : p.cor === "Dourada" ? "bg-amber-600"
                              : "bg-stone-300"
                            }`}></span>
                            {p.cor}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-ink-700">{p.fornecedor || "—"}</td>
                      <td className="px-4 py-3 font-mono text-right">{fmtNum(p.quantidade, 0)}</td>
                      <td className="px-4 py-3 font-mono text-right">{fmtBRL(p.preco_unitario)}</td>
                      <td className="px-4 py-3 font-mono text-right font-semibold">{fmtBRL(p.valor_total)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button onClick={() => openEdit(p)} className="p-1.5 rounded-md hover:bg-stone-100 text-ink-700"><Pencil size={16} /></button>
                        <button onClick={() => onDelete(p.id)} className="p-1.5 rounded-md hover:bg-red-100 text-danger"><Trash2 size={16} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg animate-fade-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-tertiary">
              <h3 className="font-heading font-bold text-ink-900">{editing ? "Editar compra" : "Nova compra"}</h3>
              <button onClick={() => setOpen(false)} className="text-ink-500"><X size={18} /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Select label="Tipo" value={form.tipo} onChange={set("tipo")} data-testid="sacaria-tipo">
                  <option value="Genérica">Genérica</option>
                  <option value="Serenata">Serenata</option>
                  <option value="Usada">Usada</option>
                  <option value="Roxa">Roxa</option>
                </Select>
                <Select label="Cor" value={form.cor} onChange={set("cor")} data-testid="sacaria-cor">
                  <option value="Amarela">Amarela</option>
                  <option value="Branca">Branca</option>
                  <option value="Azul">Azul</option>
                  <option value="Preta">Preta</option>
                  <option value="Dourada">Dourada</option>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input label="Data da compra" type="date" required value={form.data_compra} onChange={set("data_compra")} />
                <Input label="Quantidade" type="number" required value={form.quantidade} onChange={set("quantidade")} data-testid="sacaria-qtd" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input label="Preço unitário (R$)" type="number" step="0.01" required value={form.preco_unitario} onChange={set("preco_unitario")} data-testid="sacaria-preco" />
                <Input label="NF do fornecedor" value={form.nf_fornecedor} onChange={set("nf_fornecedor")} />
              </div>
              <Input label="Fornecedor" value={form.fornecedor} onChange={set("fornecedor")} />
              <div>
                <Input label="Data de pagamento (vencimento)" type="date" value={form.data_pagamento} onChange={set("data_pagamento")} data-testid="sacaria-data-pagamento" />
                <div className="text-xs text-ink-500 mt-1">
                  Se preenchida, gera automaticamente um lançamento em <b>Contas a Pagar</b> com a categoria <b>Embalagem</b>.
                </div>
              </div>
              <Input label="Observações" value={form.observacoes} onChange={set("observacoes")} />
              <div className="text-sm text-ink-500">Total: <span className="font-mono text-brand-500 font-bold text-base">{fmtBRL(total)}</span></div>
              {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button type="submit" disabled={busy} data-testid="sacaria-submit">{busy ? <Spinner size={16} /> : "Salvar"}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
